import React, { useState } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import CustomDatePicker from '../../../../Components/Utility/DatePicker/DatePicker';
import { usePOTemplateDnd } from './contextProvider/POTemplateDndContext';
import ConfirmationModal from '../../../../Components/ModalPopup/ConfirmationPopup';

export default function ControlRenderer({ control, sectionId, columnIdx, controlIdx, value, onChange, disabled = false, isPreviewMode = false }) {
  // In preview mode, make all controls appear disabled/readonly
  const { t } = useTranslation('purchase-order');
  const { dispatch, setDetailState, detailState, clearValidationError } = usePOTemplateDnd();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Generate unique field key for validation
  const fieldKey = `${sectionId}_${columnIdx}_${controlIdx}`;
  const validationError = detailState.validationErrors?.customFields?.[fieldKey];

  const optionMatchesValue = (opt, currentValue) =>
    opt?._id === currentValue || opt?.id === currentValue || opt?.value === currentValue || opt?.name === currentValue || opt?.label === currentValue;

  const resolveDropdownSelectedOption = (options, currentValue) => {
    if (!options || options.length === 0 || !currentValue) return null;
    if (typeof currentValue === 'object') return currentValue;
    return options.find((opt) => optionMatchesValue(opt, currentValue)) || null;
  };

  const getDropdownPreviewText = (options, currentValue) => {
    if (currentValue === null || currentValue === undefined || currentValue === '') return '--';
    if (typeof currentValue === 'object') {
      return currentValue?.name ?? currentValue?.label ?? currentValue?.value ?? '--';
    }
    const matched = options?.find((opt) => optionMatchesValue(opt, currentValue));
    return matched?.name ?? matched?.label ?? String(currentValue);
  };

  // If control is not enabled, show disabled state
  if (control.enable === false) {
    return (
      <div className="control-disabled">
        <span className="text-muted">{control.label || 'Control Disabled'}</span>
      </div>
    );
  }

  // If control is required but no value, show validation state
  const isRequired = control.required === true;
  // const hasValue = value && value.toString().trim() !== "";

  /**
   * Handle input change with validation
   * @param {any} newValue - New input value
   */
  const handleInputChange = (newValue) => {
    onChange(newValue);
    if (validationError && newValue && newValue.toString().trim() !== '') {
      clearValidationError('customFields', fieldKey);
    }
  };
  const renderLabel = () => {
    if (!control.label) return null;

    return (
      <p className="field-name">
        <span className={control?.isBold ? 'font-heavy' : 'font-bold'}>{control.label}</span>
        {isRequired && <span className="asterisk">*</span>}
      </p>
    );
  };

  const handleEdit = () => {
    setDetailState((prevState) => ({
      ...prevState,
      showControlPropertiesModal: true,
      editingControl: control,
      editingControlData: { sectionId, columnIdx, controlIdx },
    }));
  };

  const handleDuplicate = () => {
    const originalLabel = control.label || control?.type || 'Control';
    const duplicatedControl = {
      ...control,
      label: `${originalLabel} (Copy)`,
      id: Date.now() + Math.random() + Math.random(), // Ensure unique ID with double random for better uniqueness
      value: '', // Reset value for duplicated control
    };

    dispatch({
      type: 'ADD_CONTROL_AT_POSITION',
      sectionId,
      columnIdx,
      position: controlIdx + 1,
      control: duplicatedControl,
    });
  };

  const handleDeleteConfirm = () => {
    dispatch({ type: 'DELETE_CONTROL', sectionId, columnIdx, controlIdx });
    setShowDeleteConfirm(false);
  };

  const handleDelete = () => setShowDeleteConfirm(true);
  const handleCancelDelete = () => setShowDeleteConfirm(false);

  const renderControl = () => {
    switch (control.type) {
      case 'Textbox':
        return (
          <div className="common-input-control mb-0">
            <div className="d-flex">
              {renderLabel()}
              {!isPreviewMode && (
                <div className="d-flex ml-auto">
                  <button type="button" aria-label="Edit" onClick={handleEdit} className="potemplate-section-edit-btn">
                    <i className="icon-Edit-1" />
                  </button>
                  <button type="button" aria-label="Duplicate" onClick={handleDuplicate} className="potemplate-section-edit-btn">
                    <i className="icon-Duplicate-New" />
                  </button>
                  <button type="button" aria-label="Delete" onClick={handleDelete} className="potemplate-section-edit-btn">
                    <i className="icon-Delete" />
                  </button>
                </div>
              )}
            </div>
            <div className="d-flex position-relative">
              <input
                type="text"
                placeholder={control?.placeholder || t('PRODUCT_CRITICAL_PATH.RENDERING_MANUALITEMS.ENTER_TEXT')}
                name={control.label || 'textbox'}
                value={value || ''}
                onChange={(e) => handleInputChange(e.target.value)}
                className="form-control pr-50px"
                disabled={disabled || isPreviewMode}
                readOnly={isPreviewMode}
                maxLength={control?.characterLimit || undefined}
              />
              {control?.characterLimit && (
                <div className="text-count position-absolute-right p-2">
                  {(value || '').length}/{control?.characterLimit}
                </div>
              )}
            </div>
            {validationError && <div className="invalid-feedback">{validationError}</div>}
          </div>
        );

      case 'TextArea':
        return (
          <div className="common-input-control mb-0">
            <div className="d-flex">
              {renderLabel()}
              {!isPreviewMode && (
                <div className="d-flex ml-auto">
                  <button type="button" aria-label="Edit" onClick={handleEdit} className="potemplate-section-edit-btn">
                    <i className="icon-Edit-1" />
                  </button>
                  <button type="button" aria-label="Duplicate" onClick={handleDuplicate} className="potemplate-section-edit-btn">
                    <i className="icon-Duplicate-New" />
                  </button>
                  <button type="button" aria-label="Delete" onClick={handleDelete} className="potemplate-section-edit-btn">
                    <i className="icon-Delete" />
                  </button>
                </div>
              )}
            </div>
            <div className="d-flex">
              {isPreviewMode ? (
                <div
                  className="form-control-plaintext"
                  style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    minHeight: '60px',
                    border: '1px solid #e0e0e0',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    backgroundColor: '#f8f9fa',
                  }}
                >
                  {value || '--'}
                </div>
              ) : (
                <textarea
                  placeholder={control?.placeholder || t('PRODUCT_CRITICAL_PATH.RENDERING_MANUALITEMS.ENTER_TEXT')}
                  name={control.label || 'textarea'}
                  value={value || ''}
                  onChange={(e) => onChange(e.target.value)}
                  className="form-control h-auto resize-none"
                  disabled={disabled}
                  rows={control.rows || 3}
                />
              )}
            </div>
            {validationError && <div className="invalid-feedback">{validationError}</div>}
          </div>
        );

      case 'Dropdown':
        return (
          <div className="common-react-select mb-0">
            <div className="d-flex">
              {renderLabel()}
              {!isPreviewMode && (
                <div className="d-flex ml-auto">
                  <button type="button" aria-label="Edit" onClick={handleEdit} className="potemplate-section-edit-btn">
                    <i className="icon-Edit-1" />
                  </button>
                  <button type="button" aria-label="Duplicate" onClick={handleDuplicate} className="potemplate-section-edit-btn">
                    <i className="icon-Duplicate-New" />
                  </button>
                  <button type="button" aria-label="Delete" onClick={handleDelete} className="potemplate-section-edit-btn">
                    <i className="icon-Delete" />
                  </button>
                </div>
              )}
            </div>
            {isPreviewMode ? (
              <div className="form-control-plaintext">{getDropdownPreviewText(control?.options, value)}</div>
            ) : (
              <Select
                options={control?.options}
                getOptionLabel={(option) => option?.name || option?.label || String(option?.value ?? '')}
                getOptionValue={(option) => option?._id || option?.id}
                className="common-select"
                classNamePrefix="common-select"
                menuPosition="fixed"
                placeholder={control?.placeholder || t('PRODUCT_CRITICAL_PATH.RENDERING_MANUALITEMS.CUSTOM_SECTIONS')}
                value={resolveDropdownSelectedOption(control?.options, value)}
                isClearable
                maxMenuHeight={150}
                onChange={(e) => onChange(e)}
              />
            )}
            {validationError && <div className="invalid-feedback">{validationError}</div>}
          </div>
        );

      case 'Checkbox':
        return (
          <div className="w-100">
            <div className="d-flex">
              {renderLabel()}
              {!isPreviewMode && (
                <div className="d-flex ml-auto">
                  <button type="button" aria-label="Edit" onClick={handleEdit} className="potemplate-section-edit-btn">
                    <i className="icon-Edit-1" />
                  </button>
                  <button type="button" aria-label="Duplicate" onClick={handleDuplicate} className="potemplate-section-edit-btn">
                    <i className="icon-Duplicate-New" />
                  </button>
                  <button type="button" aria-label="Delete" onClick={handleDelete} className="potemplate-section-edit-btn">
                    <i className="icon-Delete" />
                  </button>
                </div>
              )}
            </div>
            <div className="checkbox-options">
              {control?.options?.map((opt, ind) => {
                const timeStamp = `${new Date().valueOf()}_${ind}`;
                const checkboxId = `Checkbox_${timeStamp}_${control.id}_${opt?.id || ind}`;
                return (
                  <div className="common-checkbox mb-2" key={checkboxId}>
                    <input
                      type="checkbox"
                      id={checkboxId}
                      name={`Checkbox_${control.id}`}
                      value={opt.value || opt.name}
                      onChange={(e) => {
                        if (onChange) {
                          const currentValue = Array.isArray(value) ? value : [];
                          if (e.target.checked) {
                            onChange([...currentValue, opt.value || opt.name]);
                          } else {
                            onChange(currentValue.filter((v) => v !== (opt.value || opt.name)));
                          }
                        }
                      }}
                      checked={Array.isArray(value) ? value.includes(opt.value || opt.name) : false}
                      disabled={disabled}
                    />
                    <label htmlFor={checkboxId} className="chklabel">
                      <span>{opt?.name || opt?.label}</span>
                    </label>
                  </div>
                );
              })}
            </div>
            {validationError && <div className="invalid-feedback">{validationError}</div>}
          </div>
        );

      case 'RadioButton':
        return (
          <div className="w-100">
            <div className="d-flex">
              {renderLabel()}
              {!isPreviewMode && (
                <div className="d-flex ml-auto">
                  <button type="button" aria-label="Edit" onClick={handleEdit} className="potemplate-section-edit-btn">
                    <i className="icon-Edit-1" />
                  </button>
                  <button type="button" aria-label="Duplicate" onClick={handleDuplicate} className="potemplate-section-edit-btn">
                    <i className="icon-Duplicate-New" />
                  </button>
                  <button type="button" aria-label="Delete" onClick={handleDelete} className="potemplate-section-edit-btn">
                    <i className="icon-Delete" />
                  </button>
                </div>
              )}
            </div>
            <div className="d-flex align-items-center flex-wrap">
              {control.options?.map((opt, i) => {
                // Use sectionId, columnIdx, controlIdx, and control.id to create unique group name
                // This ensures duplicated radio button controls don't interfere with each other
                const uniqueGroupId = `${sectionId}_${columnIdx}_${controlIdx}_${control.id}`;
                // Include option identifier in radioId to ensure each radio button has a unique ID
                const radioId = `radio_${uniqueGroupId}_${opt?.id || opt?.value || opt?.name || i}`;
                const groupName = `radio_group_${uniqueGroupId}`;
                return (
                  <p className="custom-radio mr-4 mb-2" key={radioId}>
                    <input
                      type="radio"
                      id={radioId}
                      name={groupName}
                      value={opt.value || opt.name}
                      onChange={(e) => onChange(e.target.value)}
                      checked={value === (opt.value || opt.name)}
                      disabled={disabled}
                    />
                    <label htmlFor={radioId}>{opt?.name || opt?.label}</label>
                  </p>
                );
              })}
            </div>
            {validationError && <div className="invalid-feedback">This field is required</div>}
          </div>
        );

      case 'DatePicker':
        return (
          <div className="common-input-control mb-0">
            <div className="d-flex">
              {renderLabel()}
              {!isPreviewMode && (
                <div className="d-flex ml-auto">
                  <button type="button" aria-label="Edit" onClick={handleEdit} className="potemplate-section-edit-btn">
                    <i className="icon-Edit-1" />
                  </button>
                  <button type="button" aria-label="Duplicate" onClick={handleDuplicate} className="potemplate-section-edit-btn">
                    <i className="icon-Duplicate-New" />
                  </button>
                  <button type="button" aria-label="Delete" onClick={handleDelete} className="potemplate-section-edit-btn">
                    <i className="icon-Delete" />
                  </button>
                </div>
              )}
            </div>
            {isPreviewMode ? (
              <div className="form-control-plaintext">
                {value ? new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '--'}
              </div>
            ) : (
              <CustomDatePicker
                keyProp={control?.label}
                dateFormat="dd-MMM-yyyy"
                valueProp={value || null}
                name={control?.label}
                placeholder={control?.placeholder}
                disable={disabled}
                onChange={(e) => onChange(e)}
              />
            )}
            {validationError && <div className="invalid-feedback">{validationError}</div>}
          </div>
        );

      case 'TableTitle':
        return (
          <div className="w-100">
            <h4 style={{ fontWeight: control.isBold ? 'bold' : 'normal' }}>{control.label || 'Table Title'}</h4>
          </div>
        );

      default:
        return (
          <div className="w-100">
            <span className="text-muted">{control.label || 'Unknown Control Type'}</span>
          </div>
        );
    }
  };

  return (
    <>
      <div className={`${disabled ? 'control-disabled' : ''}`}>{renderControl()}</div>
      {showDeleteConfirm && (
        <ConfirmationModal
          onOpen={showDeleteConfirm}
          toggle={handleCancelDelete}
          title={`${t('COMMON_LABEL.DELETE')} ${control?.label || control?.type || 'Control'}`}
          message={`${t('COMMON_LABEL.DELETE_CONFIRMATION_MESSAGE')} ${control?.label || control?.type || 'Control'} ?`}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </>
  );
}
